import axios from "axios";
import { API_BASE_URL } from "@/lib/constants/api";
import { apiClient } from "./client";
import type { components } from "./schema";

/** From OpenAPI `TokenPair` / `CustomTokenObtainPairRequest`. */
export type LoginRequest =
  components["schemas"]["CustomTokenObtainPairRequest"];
export type LoginResponse = components["schemas"]["TokenPair"];

export type RegisterRequest = components["schemas"]["RegisterRequest"];

export type RegisterAcknowledgement =
  components["schemas"]["RegisterAcknowledgement"];

/** From OpenAPI; runtime JSON may use string ids — cast at boundaries if needed. */
export type UserDetails = components["schemas"]["UserDetails"];

export type PasswordResetRequest =
  components["schemas"]["PasswordResetRequest"];
export type PasswordResetConfirmRequest =
  components["schemas"]["PasswordResetConfirmRequest"];

export type TokenPair = components["schemas"]["TokenPair"];

export type UserSession = components["schemas"]["UserSession"];
export type SessionListResponse = components["schemas"]["SessionListResponse"];
export type SessionRevokeResponse =
  components["schemas"]["SessionRevokeResponse"];

export interface MfaEnrollResponse {
  provisioning_uri: string;
  secret: string;
}

export interface MfaRecoveryCodesResponse {
  codes: string[];
}

export interface MfaDisableStepUp {
  totp_code?: string;
  password?: string;
}

export interface ImpersonateRequest {
  user_id?: string;
  company_id?: string;
  totp_code?: string;
}

export interface ImpersonateResponse {
  message: string;
  user: UserDetails;
  tokens: {
    refresh: string;
    access: string;
  };
}

/**
 * SSOConnection primary key. An integer today; a UUID string once the backend
 * migration lands. Number support exists so this can merge ahead of the backend
 * change, with no window where login is broken, and can be dropped once that
 * has landed.
 */
export type SSOConnectionId = number | string;

export interface SSOLookupResponse {
  connection_id: SSOConnectionId;
  provider: string;
  protocol: string;
  org_type: string;
  enforced: boolean;
}

/**
 * The public answer to "does this address sign in through SSO?", asked by the
 * login page before anyone is signed in. It says no more than a password
 * attempt's `sso_required` refusal already does. Not in the generated schema
 * yet; typed here from the endpoint's contract.
 */
export type SSODiscoverResponse =
  | {
      sso: true;
      provider: string;
      /** The same identifier a `sso_required` refusal carries. */
      connection_id: SSOConnectionId;
      enforced: boolean;
      /** Not part of the answer today; honoured if the server ever sends it. */
      protocol?: string;
    }
  | { sso: false };

/**
 * The password-change answer. Every session is signed out by the change, this
 * browser's included, so `tokens` is the new session this browser carries on
 * with. Typed here until the schema describes the response.
 */
export interface PasswordChangeResponse {
  message: string;
  tokens: { access: string; refresh: string };
}

export type SSOGroupMapping = { idp_group: string; ec_role: string };

export interface SSOConnection {
  id: SSOConnectionId;
  org_type: string;
  org_name: string | null;
  /**
   * Owning organization. One FK covers every level — `org_type` says which
   * level it is. Null means the connection is system-scoped (platform-wide).
   * UUID, because organization PKs are UUIDs.
   */
  organization?: string | null;
  provider: string;
  protocol: string;
  client_id: string;
  issuer_url: string;
  email_domains: string[];
  group_mappings: SSOGroupMapping[];
  /** Role JIT-provisioned users get. Blank = the org-type default (clamped by the JIT ceiling). */
  default_role?: string | null;
  jit_enabled: boolean;
  enforced: boolean;
  scim_enabled: boolean;
  /** Offer this org's IdP directory in the add-user picker. */
  directory_enabled: boolean;
  /** A directory read could authenticate right now. */
  directory_ready: boolean;
  /** Google only: an admin has completed the Connect flow. */
  directory_connected: boolean;
  /** Google fallback: a service account key is stored (never sent back). */
  directory_has_service_account: boolean;
  directory_impersonate_subject: string;
  is_active: boolean;
}

export interface SSOConnectionPayload {
  /**
   * Organization to attach the connection to, at any level. Omit for a
   * system-scoped connection. The backend derives the connection's scope from
   * this organization's `type`, so there is no separate per-level FK to pick.
   */
  organization?: string;
  provider: string;
  protocol: string;
  client_id?: string;
  client_secret?: string;
  issuer_url?: string;
  email_domains: string[];
  group_mappings?: SSOGroupMapping[];
  /** Role JIT-provisioned users get. Blank = the org-type default (clamped by the JIT ceiling). */
  default_role?: string;
  jit_enabled?: boolean;
  enforced?: boolean;
  is_active?: boolean;
}

export interface SSODiscoveryResult {
  ok: boolean;
  discovery_url: string;
  issuer?: string | null;
  issuer_matches?: boolean | null;
  authorization_endpoint?: string | null;
  token_endpoint?: string | null;
  userinfo_endpoint?: string | null;
  jwks_uri?: string | null;
  end_session_endpoint?: string | null;
  scopes_supported?: string[];
  response_types_supported?: string[];
  grant_types_supported?: string[];
  error?: string | null;
  error_detail?: string | null;
}

export interface SSOTestCheck {
  name: string;
  passed: boolean;
  detail: string;
}

export interface SSOTestResult {
  ok: boolean;
  checks: SSOTestCheck[];
  /** Callback URL the caller must register on the IdP side. */
  expected_redirect_uri?: string;
}

export interface SSOTestOverrides {
  client_id?: string;
  client_secret?: string;
  issuer_url?: string;
}

export interface SCIMTokenResult {
  scim_bearer_token: string;
  detail: string;
}

export const ssoConnectionsApi = {
  list: async (): Promise<SSOConnection[]> => {
    const res = await apiClient.get("/sso-connections/");
    return res.data.results ?? res.data;
  },
  create: async (payload: SSOConnectionPayload): Promise<SSOConnection> => {
    const res = await apiClient.post("/sso-connections/", payload);
    return res.data;
  },
  update: async (
    id: SSOConnectionId,
    payload: Partial<SSOConnectionPayload>,
  ): Promise<SSOConnection> => {
    const res = await apiClient.patch(`/sso-connections/${id}/`, payload);
    return res.data;
  },
  delete: async (id: SSOConnectionId): Promise<void> => {
    await apiClient.delete(`/sso-connections/${id}/`);
  },
  /** Probe an OIDC issuer's .well-known discovery document. */
  discover: async (issuerUrl: string): Promise<SSODiscoveryResult> => {
    const res = await apiClient.post(
      "/sso-connections/discover/",
      { issuer_url: issuerUrl },
      // Backend returns 400 on discovery failure with a structured body; surface it instead of throwing.
      { validateStatus: (s) => s === 200 || s === 400 },
    );
    return res.data;
  },
  /** Run the connection-test probe (discovery + credentials) for a stored connection. */
  test: async (
    id: SSOConnectionId,
    overrides: SSOTestOverrides = {},
  ): Promise<SSOTestResult> => {
    const res = await apiClient.post(
      `/sso-connections/${id}/test/`,
      overrides,
      { validateStatus: (s) => s === 200 || s === 400 },
    );
    return res.data;
  },
  /** Generate a new SCIM bearer token for this connection. Token is returned once. */
  generateScimToken: async (id: SSOConnectionId): Promise<SCIMTokenResult> => {
    const res = await apiClient.post(
      `/sso-connections/${id}/generate-scim-token/`,
    );
    return res.data;
  },
  /**
   * The link to send this organization's IdP admin so they can authorise
   * directory reads. We cannot grant ourselves this — only their admin can,
   * on their own provider's consent screen.
   */
  directoryConsentUrl: async (
    id: SSOConnectionId,
  ): Promise<{ url: string; provider: string; redirect_uri: string }> => {
    const res = await apiClient.post(
      `/sso-connections/${id}/directory-consent-url/`,
    );
    return res.data;
  },
  /** Forget the stored Google authorisation. Sign-in is unaffected. */
  directoryDisconnect: async (id: SSOConnectionId): Promise<void> => {
    await apiClient.post(`/sso-connections/${id}/directory-disconnect/`);
  },
};

export const authApi = {
  login: async (data: LoginRequest): Promise<LoginResponse> => {
    const response = await apiClient.post("/auth/login/", data);
    return response.data;
  },

  logout: async (refreshToken: string): Promise<void> => {
    await apiClient.post("/auth/logout/", { refresh: refreshToken });
  },

  register: async (data: RegisterRequest): Promise<RegisterAcknowledgement> => {
    const response = await apiClient.post("/auth/register/", data);
    return response.data;
  },

  getMe: async (): Promise<UserDetails> => {
    const response = await apiClient.get("/auth/me/");
    return response.data;
  },

  updateMe: async (data: Partial<UserDetails>): Promise<UserDetails> => {
    const response = await apiClient.patch("/auth/me/", data);
    return response.data;
  },

  /** Upload a profile photo; the API checks it is an image. */
  uploadAvatar: async (file: File): Promise<UserDetails> => {
    const form = new FormData();
    form.append("avatar", file);
    const response = await apiClient.patch("/auth/me/", form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  },

  /** Remove the profile photo, so the generated picture shows again. */
  removeAvatar: async (): Promise<UserDetails> => {
    const response = await apiClient.patch("/auth/me/", { avatar: null });
    return response.data;
  },

  changePassword: async (data: {
    old_password: string;
    new_password: string;
    new_password_confirm: string;
  }): Promise<PasswordChangeResponse> => {
    const response = await apiClient.post("/auth/password/change/", data);
    return response.data;
  },

  requestPasswordReset: async (data: PasswordResetRequest): Promise<void> => {
    await apiClient.post("/auth/password/reset/", data);
  },

  /**
   * Is this reset link still usable?
   *
   * Asked before the reset page renders its form, so an expired, superseded or
   * already-used link is refused up front instead of after the reader has
   * chosen and typed a new password twice. Read-only — it does not consume the
   * token. Rejects with the same `token_expired` / `token_invalid` codes the
   * confirm call raises.
   */
  validatePasswordResetToken: async (token: string): Promise<void> => {
    await apiClient.post("/auth/password/reset/validate/", { token });
  },

  confirmPasswordReset: async (
    data: PasswordResetConfirmRequest,
  ): Promise<void> => {
    await apiClient.post("/auth/password/reset/confirm/", data);
  },

  // Schema declares `requestBody?: never` for `/auth/email/verify/` despite
  // the docstring referencing an HMAC token from allauth's
  // `EmailConfirmationHMAC`. Inferred body shape is `{ key: <hmac_token> }`
  // per allauth convention. Halt + escalate if runtime rejects.
  verifyEmail: async (key: string): Promise<void> => {
    await apiClient.post("/auth/email/verify/", { token: key });
  },

  // Authenticated endpoint; back identifies the user from JWT. Rate-limited
  // 1 request per minute per user; 429 surfaces `rate_limited` envelope.
  resendVerificationEmail: async (): Promise<void> => {
    await apiClient.post("/auth/email/resend/");
  },

  changeEmail: async (data: {
    new_email: string;
    password: string;
  }): Promise<void> => {
    await apiClient.post("/auth/email/change/", data);
  },

  confirmEmailChange: async (token: string): Promise<void> => {
    await apiClient.post("/auth/email/change/confirm/", { token });
  },

  // MFA endpoints — schema declares `requestBody?: never` for all 6 paths
  // despite the docstrings describing input. Body shapes inferred per
  // back-side conventions. Halt + escalate if runtime rejects.

  mfaEnroll: async (): Promise<MfaEnrollResponse> => {
    const response = await apiClient.post("/auth/mfa/enroll/");
    return response.data;
  },

  // Returns a re-minted pre-MFA token pair whose access token now carries
  // mfa_enrolled=true, so the SPA can swap it before routing to the challenge.
  mfaEnrollConfirm: async (totpCode: string): Promise<TokenPair> => {
    const response = await apiClient.post("/auth/mfa/enroll/confirm/", {
      code: totpCode,
    });
    return response.data;
  },

  /** Put off enrolling: trades the short pre-MFA pair for a normal, deferred session. */
  mfaSkip: async (refreshToken: string | null): Promise<TokenPair> => {
    const response = await apiClient.post(
      "/auth/mfa/skip/",
      refreshToken ? { refresh: refreshToken } : {},
    );
    return response.data;
  },

  /**
   * Complete the challenge. `refreshToken` is the pair being replaced: sent so
   * the server ends that session, and a sign-in leaves one session behind
   * rather than the verified one plus the pre-MFA one it came from.
   */
  mfaVerify: async (
    totpCode: string,
    refreshToken?: string | null,
  ): Promise<TokenPair> => {
    const response = await apiClient.post("/auth/mfa/verify/", {
      code: totpCode,
      ...(refreshToken ? { refresh: refreshToken } : {}),
    });
    return response.data;
  },

  mfaGenerateRecoveryCodes: async (): Promise<MfaRecoveryCodesResponse> => {
    const response = await apiClient.post("/auth/mfa/recovery-codes/");
    return response.data;
  },

  /** Complete the challenge with a recovery code; `refreshToken` as for `mfaVerify`. */
  mfaUseRecoveryCode: async (
    code: string,
    refreshToken?: string | null,
  ): Promise<TokenPair> => {
    const response = await apiClient.post("/auth/mfa/recovery-codes/use/", {
      code,
      ...(refreshToken ? { refresh: refreshToken } : {}),
    });
    return response.data;
  },

  mfaDisable: async (stepUp: MfaDisableStepUp): Promise<void> => {
    await apiClient.post("/auth/mfa/disable/", stepUp);
  },

  impersonate: async (
    data: ImpersonateRequest,
  ): Promise<ImpersonateResponse> => {
    const response = await apiClient.post("/auth/impersonate/", data);
    return response.data;
  },

  stopImpersonate: async (): Promise<ImpersonateResponse> => {
    const response = await apiClient.post("/auth/stop-impersonate/");
    return response.data;
  },

  ssoLookup: async (email: string): Promise<SSOLookupResponse> => {
    const response = await apiClient.get(
      `/sso-connections/lookup/?email=${encodeURIComponent(email)}`,
    );
    return response.data;
  },

  /**
   * Whether an address signs in through SSO, for the login page.
   *
   * Sent without the shared client on purpose. The endpoint is public, and the
   * client would stamp a leftover token from an ended session onto it: the
   * server answers a bad token with 401 even on a public route, and the
   * client's 401 handling would then refresh, fail and reload the login page
   * the visitor is typing into.
   */
  ssoDiscover: async (email: string): Promise<SSODiscoverResponse> => {
    const response = await axios.get(`${API_BASE_URL}/auth/sso/discover/`, {
      params: { email },
    });
    return response.data;
  },

  listSessions: async (): Promise<SessionListResponse> => {
    const response = await apiClient.get("/auth/sessions/");
    return response.data;
  },

  revokeSession: async (jti: string): Promise<SessionRevokeResponse> => {
    const response = await apiClient.post("/auth/sessions/revoke/", { jti });
    return response.data;
  },

  revokeCurrentSession: async (): Promise<SessionRevokeResponse> => {
    const response = await apiClient.post("/auth/sessions/revoke/", {
      sign_out_current: true,
    });
    return response.data;
  },

  revokeOtherSessions: async (): Promise<SessionRevokeResponse> => {
    const response = await apiClient.post("/auth/sessions/revoke-others/", {});
    return response.data;
  },
};
